import { ServiceLevel } from '@provision/service-level-utils';
import { EscalationRule } from '@provision/sidekick-utils';
import { SignalUrgencyLevel } from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { ESCALATION_URGENCY_CONFIG, serializeEscalationRules } from './escalation-utils';

interface DefaultEscalationPolicy {
  title: string;
  description: string;
  urgencyLevel: string;
  escalationRules: string;
}

const DEFAULT_NORMAL_RULES: EscalationRule[] = [
  {
    id: 'normal-all-slas-business-hours',
    order: 0,
    name: 'All SLAs - during business hours',
    serviceLevels: 'all',
    timeCondition: { type: 'business_hours' },
    response: { type: 'by_end_of_day' },
  },
  {
    id: 'normal-all-slas-outside-business-hours',
    order: 1,
    name: 'All SLAs - outside business hours',
    serviceLevels: 'all',
    timeCondition: { type: 'outside_business_hours' },
    response: { type: 'by_next_business_day', time: '12:00' },
  },
];

const PAID_SERVICE_LEVELS: string[] = [
  ServiceLevel.SecurityOnly,
  ServiceLevel.Priority,
  ServiceLevel.Proactive,
  ServiceLevel.Signature,
];

const DEFAULT_URGENT_RULES: EscalationRule[] = [
  {
    id: 'urgent-paid-slas-9am-10pm',
    order: 0,
    name: 'Secure/Priority/Proactive/Signature: 9am-10pm',
    serviceLevels: PAID_SERVICE_LEVELS,
    timeCondition: {
      type: 'custom',
      customSchedule: [
        { weekDay: 0, timeFrom: '09:00', timeTo: '22:00' },
        { weekDay: 1, timeFrom: '09:00', timeTo: '22:00' },
        { weekDay: 2, timeFrom: '09:00', timeTo: '22:00' },
        { weekDay: 3, timeFrom: '09:00', timeTo: '22:00' },
        { weekDay: 4, timeFrom: '09:00', timeTo: '22:00' },
        { weekDay: 5, timeFrom: '09:00', timeTo: '22:00' },
        { weekDay: 6, timeFrom: '09:00', timeTo: '22:00' },
      ],
    },
    response: { type: 'fixed_duration', durationMinutes: 60 },
  },
  {
    id: 'urgent-paid-slas-overnight',
    order: 1,
    name: 'Secure/Priority/Proactive/Signature: Overnight',
    serviceLevels: PAID_SERVICE_LEVELS,
    timeCondition: {
      type: 'custom',
      customSchedule: [
        { weekDay: 0, timeFrom: '22:00', timeTo: '23:59' },
        { weekDay: 1, timeFrom: '22:00', timeTo: '23:59' },
        { weekDay: 2, timeFrom: '22:00', timeTo: '23:59' },
        { weekDay: 3, timeFrom: '22:00', timeTo: '23:59' },
        { weekDay: 4, timeFrom: '22:00', timeTo: '23:59' },
        { weekDay: 5, timeFrom: '22:00', timeTo: '23:59' },
        { weekDay: 6, timeFrom: '22:00', timeTo: '23:59' },
      ],
    },
    response: { type: 'by_tomorrow', time: '10:00' },
  },
  {
    id: 'urgent-paid-slas-early-morning',
    order: 2,
    name: 'Secure/Priority/Proactive/Signature: Early morning',
    serviceLevels: PAID_SERVICE_LEVELS,
    timeCondition: {
      type: 'custom',
      customSchedule: [
        { weekDay: 0, timeFrom: '00:00', timeTo: '09:00' },
        { weekDay: 1, timeFrom: '00:00', timeTo: '09:00' },
        { weekDay: 2, timeFrom: '00:00', timeTo: '09:00' },
        { weekDay: 3, timeFrom: '00:00', timeTo: '09:00' },
        { weekDay: 4, timeFrom: '00:00', timeTo: '09:00' },
        { weekDay: 5, timeFrom: '00:00', timeTo: '09:00' },
        { weekDay: 6, timeFrom: '00:00', timeTo: '09:00' },
      ],
    },
    response: { type: 'by_fixed_time', time: '10:00' },
  },
  {
    id: 'urgent-paid-slas-holidays',
    order: 3,
    name: 'Secure/Priority/Proactive/Signature: Holidays',
    serviceLevels: PAID_SERVICE_LEVELS,
    timeCondition: { type: 'outside_business_hours' },
    response: { type: 'best_effort' },
  },
];

const DEFAULT_LIFE_SAFETY_RULES: EscalationRule[] = [
  {
    id: 'life-safety-within-60-minutes',
    order: 0,
    name: 'Any time',
    serviceLevels: 'all',
    timeCondition: { type: 'always' },
    response: { type: 'fixed_duration', durationMinutes: 60 },
  },
];

const DEFAULT_RULES_BY_URGENCY: Record<string, EscalationRule[]> = {
  [SignalUrgencyLevel.Normal]: DEFAULT_NORMAL_RULES,
  [SignalUrgencyLevel.Urgent]: DEFAULT_URGENT_RULES,
  [SignalUrgencyLevel.LifeSafety]: DEFAULT_LIFE_SAFETY_RULES,
};

const DEFAULT_DESCRIPTION_BY_URGENCY: Record<string, string> = {
  [SignalUrgencyLevel.Normal]:
    '<ul><li>All SLAs (business hours): by end of day</li><li>All SLAs (outside business hours): by 12:00 next business day</li></ul>',
  [SignalUrgencyLevel.Urgent]:
    '<ul><li>Secure +3 more (9am-10pm): within 60 minutes</li><li>Secure +3 more (overnight): by 10:00 tomorrow</li><li>Secure +3 more (early morning): by 10:00</li><li>Secure +3 more (holidays): best effort</li></ul>',
  [SignalUrgencyLevel.LifeSafety]: '<ul><li>All SLAs (any time): within 60 minutes</li></ul>',
};

export function getDefaultEscalationTitle(urgencyLevel: string): string {
  const config = ESCALATION_URGENCY_CONFIG.find((c) => (c.value as string) === urgencyLevel);
  return config?.formLabel ?? 'Escalation';
}

export function buildDefaultEscalationPolicy(urgencyLevel: string): DefaultEscalationPolicy {
  const rules = DEFAULT_RULES_BY_URGENCY[urgencyLevel] ?? DEFAULT_LIFE_SAFETY_RULES;
  return {
    title: getDefaultEscalationTitle(urgencyLevel),
    description:
      DEFAULT_DESCRIPTION_BY_URGENCY[urgencyLevel] ??
      DEFAULT_DESCRIPTION_BY_URGENCY[SignalUrgencyLevel.LifeSafety],
    urgencyLevel,
    escalationRules: serializeEscalationRules(rules),
  };
}

export function getEnabledUrgencyLevels(disabledUrgencies: string[]): string[] {
  return ESCALATION_URGENCY_CONFIG.filter(({ value }) => {
    if (value === SignalUrgencyLevel.Normal) return true;
    return !disabledUrgencies.includes(value);
  }).map(({ value }) => value);
}
